import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSaveForm } from '@/shared/lib/useSaveForm';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { FormField } from '@/shared/ui/FormField';
import { ModalDialog } from '@/shared/ui/ModalDialog';
import type { SelectOption } from '@/shared/ui/selectTypes';

export interface EntityField {
  name: string;
  label: string;
  type?: 'text' | 'textarea' | 'number' | 'checkbox' | 'select';
  options?: SelectOption[];
  placeholder?: string;
  /** Server error key when it differs from `name`. */
  errorKey?: string;
}

export type EntityValues = Record<string, string | boolean>;

type ModalProps = {
  title: string;
  fields: EntityField[];
  initial: EntityValues;
  save: (values: EntityValues) => Promise<unknown>;
  /** Query key prefix of the list to refetch after saving. */
  listKey: string;
  onClose: () => void;
};

const FIELD_WIDTH = 250;

export const EntityFormModal = ({ title, fields, initial, save, listKey, onClose }: ModalProps) => {
  const qc = useQueryClient();
  const [values, setValues] = useState<EntityValues>(initial);

  const form = useSaveForm(save, () => {
    void qc.invalidateQueries({ queryKey: [listKey] });
    onClose();
  });

  const setValue = (field: EntityField, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field.name]: value }));
    form.clearErrors(field.errorKey ?? field.name);
  };

  const renderInput = (field: EntityField) => {
    const value = values[field.name];
    switch (field.type) {
      case 'checkbox':
        return (
          <div className="checkbox">
            <label>
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => setValue(field, e.target.checked)}
              />
              {field.placeholder ?? 'Да'}
            </label>
          </div>
        );
      case 'select':
        return (
          <FilterSelect
            options={field.options ?? []}
            value={String(value)}
            isClearable={false}
            onChange={(v) => setValue(field, v)}
          />
        );
      case 'textarea':
        return (
          <textarea
            className="form-control input-sm"
            rows={4}
            placeholder={field.placeholder}
            value={String(value)}
            onChange={(e) => setValue(field, e.target.value)}
          />
        );
      default:
        return (
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            className="form-control input-sm"
            style={{ width: FIELD_WIDTH }}
            placeholder={field.placeholder}
            value={String(value)}
            onChange={(e) => setValue(field, e.target.value)}
          />
        );
    }
  };

  return (
    <ModalDialog title={title} onClose={onClose} isSaving={form.isPending} onSave={() => form.save(values)}>
      {fields.map((field) => (
        <FormField key={field.name} label={field.label} errors={form.errors[field.errorKey ?? field.name]}>
          {renderInput(field)}
        </FormField>
      ))}
      {form.errors.common && <p className="text-red">{form.errors.common[0]}</p>}
    </ModalDialog>
  );
};

type ButtonProps = Omit<ModalProps, 'onClose'> & {
  buttonLabel: string;
  buttonClassName?: string;
};

/** Button opening an `EntityFormModal` (create in the header, edit inside a table cell). */
export const FormModalButton = ({
  buttonLabel,
  buttonClassName = 'btn btn-success btn-sm',
  ...modal
}: ButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button type="button" className={buttonClassName} onClick={() => setIsOpen(true)}>
        {buttonLabel}
      </button>
      {isOpen && <EntityFormModal {...modal} onClose={() => setIsOpen(false)} />}
    </>
  );
};
